import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCalendar } from '../../assets/icons/IconCalendar';
import { IconChevron } from '../../assets/icons/IconChevron';
import { useSentRelanceEvents } from '../../hooks/useSentRelanceEvents';
import { TranslationKey } from '../../i18n/translationKeys';
import { useLanguageStore } from '../../stores/languageStore';
import { describeSentRelance } from '../../utils/relance/describeSentRelance';

interface SentRelancesDropdownProps {
    customerId: string;
}

export const SentRelancesDropdown: React.FC<SentRelancesDropdownProps> = ({ customerId }) => {
    const { t } = useTranslation();
    const lang = useLanguageStore(s => s.lang);
    const { state, events } = useSentRelanceEvents(customerId);
    const [open, setOpen] = useState(false);
    const panelId = useId();

    // Advisory: nothing shows until the sends are in, and a failed read just leaves it out.
    if (state !== 'ready' || events.length === 0) return null;

    return (
        <div data-testid="followup-sent-relances" className="self-stretch rounded-lg border border-line text-s font-normal text-content">
            <button
                type="button"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpen(o => !o)}
                data-testid="followup-sent-relances-toggle"
                className="flex items-center gap-xs w-full px-m py-s text-left text-s font-normal rounded-lg cursor-pointer focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring"
            >
                <IconCalendar className="shrink-0 text-content-secondary" />
                <span>{t(TranslationKey.CustomerFollowUp.FollowUps.Toggle, { count: events.length })}</span>
                <IconChevron className={`shrink-0 transition-transform ${open ? '-rotate-90' : 'rotate-90'}`} />
            </button>
            {open && (
                <div id={panelId} className="px-m py-m border-t border-line-subtle">
                    <ul className="flex flex-col gap-m" data-testid="followup-sent-relances-list">
                        {events.map((event, index) => {
                            const { date, label, invoiceLine } = describeSentRelance(event, events.length - index, t, lang);
                            return (
                                <li key={event.id} className="flex gap-s" data-testid={`followup-sent-relance-${event.id}`}>
                                    <span aria-hidden="true">•</span>
                                    <div className="min-w-0">
                                        <p>{date} - {label}</p>
                                        <p className="text-content-secondary">{invoiceLine}</p>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};
